import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VoiceChat as VirtualCoachIcon } from '@mui/icons-material';
import { IconButton, } from '@mui/material';
import { getSignedAssetUrl, useStore } from '@mcn-platform/models';
import { ConversationDialog, conversationDialogDefaults, conversationDialogMeta, ConversationStoreProvider, Tokens, } from '@mcn-platform/converse-lib';
import ConversationStoreAdapter from './ConversationStoreAdapter';
const CoachFooterWidget = ({ title = 'Virtual Coach', showItemReferences = false, iconAssetId, backgroundColor, padding, courseCardShowImage, courseCardShowTitle, courseCardShowInstructor, courseCardShowDescLines, courseCardShowPrice, clientAIAnimatedProcessingIcon, openWebPagesInNewTab = false, hideRightRailOnMobile = false, maxItemsInRightRail, questionStyle, responseStyle, inputStyle, startingMessageFontStyle, popupPosition, sendImage, enableVoiceInput, resetConversationLabel, windowStyle, headerStyle, enableMultiRefinements, multiRefinementSubmitButtonText, agentName, }) => {
    const [open, setOpen] = useState(false);
    const store = useStore();
    const navigate = useNavigate();
    const onPortalRelativeNavigate = useCallback((relativePath) => {
        if (relativePath)
            navigate(relativePath);
    }, [navigate]);
    const [iconAssetUrl, setIconUrl] = useState(null);
    const handleClose = () => {
        setOpen(false);
    };
    const descriptionElementRef = React.useRef(null);
    useEffect(() => {
        if (open) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [open]);
    useEffect(() => {
        if (iconAssetId) {
            const loadAssetUrl = async () => {
                try {
                    const iconAsset = await store.load('asset', iconAssetId);
                    const url = await getSignedAssetUrl(iconAsset, 'original', store);
                    if (url) {
                        setIconUrl(url);
                    }
                    else {
                        setIconUrl(null);
                    }
                }
                catch (error) {
                    console.error('Failed to get signed asset URL:', error);
                    setIconUrl(null);
                }
            };
            loadAssetUrl();
        }
        else {
            setIconUrl(null);
        }
    }, [iconAssetId, store]);
    const tokens = useMemo(() => {
        if (!store.currentPortal)
            return null;
        const tokensInstance = new Tokens(store.currentPortal);
        const locale = store.customerContext?.currentPage?.locale || store.customerContext?.locale;
        if (locale) {
            tokensInstance.setLocale(locale);
        }
        return tokensInstance;
    }, [
        store.currentPortal,
        store.customerContext?.currentPage?.locale,
        store.customerContext?.locale,
    ]);
    const messageConfig = useMemo(() => ({
        showItemReferences,
        questionStyle,
        responseStyle,
        openWebPagesInNewTab,
        onPortalRelativeNavigate,
        hideRightRailOnMobile,
        maxItemsInRightRail,
        enableMultiRefinements,
        multiRefinementSubmitButtonText,
        openSupportToolDelay: store.clientAIConfig?.openSupportToolDelay || 6000,
        openSupportToolMessage: tokens ? tokens.resolvePortalText(store.clientAIConfig?.openSupportToolMessage, 'openSupportToolMessage') : store.clientAIConfig?.openSupportToolMessage || '{{openSupportToolMessage}}',
    }), [
        showItemReferences,
        questionStyle,
        responseStyle,
        openWebPagesInNewTab,
        onPortalRelativeNavigate,
        hideRightRailOnMobile,
        maxItemsInRightRail,
        enableMultiRefinements,
        multiRefinementSubmitButtonText,
        store.clientAIConfig?.openSupportToolDelay,
        store.clientAIConfig?.openSupportToolMessage,
        tokens,
    ]);
    const mediaCardConfig = useMemo(() => ({
        courseCardShowPrice,
        courseCardShowTitle: courseCardShowTitle || true,
        courseCardShowInstructor: courseCardShowInstructor || false,
        courseCardShowImage: courseCardShowImage || false,
        courseCardShowDesc: (courseCardShowDescLines && courseCardShowDescLines > 0) || true,
        courseCardMaxLinesDescription: (courseCardShowDescLines || 2),
        courseCardShowInfoBar: false,
        courseCardShowActionButton: true,
    }), [
        courseCardShowImage,
        courseCardShowPrice,
        courseCardShowTitle,
        courseCardShowInstructor,
        courseCardShowDescLines,
    ]);
    return (_jsxs(ConversationStoreProvider, { value: new ConversationStoreAdapter(store), children: [_jsx(ConversationDialog, { title: title, open: open, handleClose: handleClose, messageConfig: messageConfig, mediaCardConfig: mediaCardConfig, inputStyle: inputStyle, startingMessageFontStyle: startingMessageFontStyle, popupPosition: popupPosition, sendImage: sendImage, windowStyle: windowStyle, headerStyle: headerStyle, resetConversationLabel: resetConversationLabel, animatedProcessingImage: clientAIAnimatedProcessingIcon, enableVoiceInput: Boolean(enableVoiceInput), agentName: agentName }), _jsx(IconButton, { onClick: () => setOpen(!open), children: iconAssetUrl ? (_jsx("img", { src: iconAssetUrl, alt: "Chat Icon", style: { width: '100%', height: '100%', padding } })) : (_jsx(VirtualCoachIcon, { sx: {
                        color: (t) => t.portal.footer?.socialLinks?.color,
                        backgroundColor,
                        padding,
                    } })) })] }));
};
CoachFooterWidget.defaultProps = {
    ...conversationDialogDefaults,
    iconAssetId: undefined,
    backgroundColor: undefined,
    padding: 1,
};
CoachFooterWidget.meta = {
    group: 'portal',
    name: 'Virtual Coach Footer Widget',
    sort: 40,
    description: 'Chatbot for page to answer questions.',
    props: [
        {
            key: 'iconAssetId',
            name: 'Chat Icon URL',
            type: 'asset',
            description: 'Custom icon image for the chat bubble.',
        },
        {
            key: 'backgroundColor',
            name: 'Background Color',
            type: 'color',
            description: 'Background color for the chat icon.',
        },
        {
            key: 'padding',
            name: 'Icon Padding',
            type: 'number',
            description: 'Padding around the icon inside the chat bubble.',
        },
        ...conversationDialogMeta,
    ],
    defaultConfig: {
        type: 'component',
        component: 'VirtualCoachFooterWidget',
        props: CoachFooterWidget.defaultProps,
    },
};
export default CoachFooterWidget;
//# sourceMappingURL=CoachFooterWidget.js.map